import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Edit3, Power, Save, X, RefreshCw, Search, Tag, Calendar, Filter } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { ModalPortal } from "./ModalPortal";

const STATUS_PILL = {
    active: { label: "Active", cls: "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" },
    scheduled: { label: "Scheduled", cls: "bg-blue-500/10 border-blue-500/30 text-blue-200" },
    expired: { label: "Expired", cls: "bg-white/5 border-white/10 text-white/40" },
    disabled: { label: "Disabled", cls: "bg-red-500/10 border-red-500/30 text-red-200" },
};

const DISCOUNT_TYPES = [
    { id: "percentage", label: "Percentage off", suffix: "%" },
    { id: "fixed", label: "Fixed amount off", suffix: "A$" },
    { id: "free_shipping", label: "Free shipping", suffix: "" },
];

const APPLIES_TO = [
    { id: "all", label: "Entire store" },
    { id: "categories", label: "Specific categories" },
    { id: "products", label: "Specific products" },
];

const emptyForm = () => ({
    code: "",
    description: "",
    discount_type: "percentage",
    value: 10,
    starts_at: "",
    ends_at: "",
    min_order_value: "",
    applies_to: "all",
    category_slugs: [],
    product_slugs: [],
    usage_limit: "",
    usage_limit_per_customer: "",
    is_active: true,
});

const toLocalDtInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
const toISO = (local) => (local ? new Date(local).toISOString() : null);

export const AdminDiscountsPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [discounts, setDiscounts] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [editing, setEditing] = useState(null); // discount code being edited (string) or "new"
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [d, p, c] = await Promise.all([
                api.get("/admin/discounts", { headers }),
                api.get("/admin/products", { headers }),
                api.get("/categories"),
            ]);
            setDiscounts(d.data);
            setProducts(p.data);
            setCategories(c.data);
        } catch {
            toast.error("Failed to load discounts");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const openNew = () => {
        setEditing("new");
        setForm(emptyForm());
    };

    const openEdit = (d) => {
        setEditing(d.code);
        setForm({
            code: d.code,
            description: d.description || "",
            discount_type: d.discount_type,
            value: d.value || 0,
            starts_at: toLocalDtInput(d.starts_at),
            ends_at: toLocalDtInput(d.ends_at),
            min_order_value: d.min_order_value ?? "",
            applies_to: d.applies_to || "all",
            category_slugs: d.category_slugs || [],
            product_slugs: d.product_slugs || [],
            usage_limit: d.usage_limit ?? "",
            usage_limit_per_customer: d.usage_limit_per_customer ?? "",
            is_active: d.is_active !== false,
        });
    };

    const save = async () => {
        if (!form.code.trim()) {
            toast.error("Code is required");
            return;
        }
        if (form.discount_type !== "free_shipping" && (!form.value || Number(form.value) <= 0)) {
            toast.error("Discount value must be greater than 0");
            return;
        }
        setSaving(true);
        const payload = {
            code: form.code.trim().toUpperCase(),
            description: form.description || null,
            discount_type: form.discount_type,
            value: form.discount_type === "free_shipping" ? 0 : Number(form.value),
            starts_at: toISO(form.starts_at),
            ends_at: toISO(form.ends_at),
            min_order_value: form.min_order_value === "" ? null : Number(form.min_order_value),
            applies_to: form.applies_to,
            category_slugs: form.applies_to === "categories" ? form.category_slugs : [],
            product_slugs: form.applies_to === "products" ? form.product_slugs : [],
            usage_limit: form.usage_limit === "" ? null : Number(form.usage_limit),
            usage_limit_per_customer: form.usage_limit_per_customer === "" ? null : Number(form.usage_limit_per_customer),
            is_active: form.is_active,
        };
        try {
            if (editing === "new") {
                await api.post("/admin/discounts", payload, { headers });
                toast.success(`Code ${payload.code} created`);
            } else {
                await api.patch(`/admin/discounts/${editing}`, payload, { headers });
                toast.success(`Code ${payload.code} updated`);
            }
            setEditing(null);
            await load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(Array.isArray(detail) ? detail.map((e) => e.msg).join(" ") : (detail || "Save failed"));
        }
        setSaving(false);
    };

    const toggle = async (code) => {
        try {
            await api.post(`/admin/discounts/${code}/toggle`, {}, { headers });
            await load();
        } catch {
            toast.error("Toggle failed");
        }
    };

    const del = async (code) => {
        if (!window.confirm(`Permanently delete ${code}? Customers who already used it will keep their discount on past orders.`)) return;
        try {
            await api.delete(`/admin/discounts/${code}`, { headers });
            toast.success(`Code ${code} deleted`);
            await load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const filtered = discounts.filter((d) => {
        if (statusFilter !== "all" && d.status !== statusFilter) return false;
        if (filter && !d.code.toLowerCase().includes(filter.toLowerCase()) && !(d.description || "").toLowerCase().includes(filter.toLowerCase())) return false;
        return true;
    });

    const toggleSlug = (slug) => {
        setForm((f) => ({
            ...f,
            product_slugs: f.product_slugs.includes(slug)
                ? f.product_slugs.filter((s) => s !== slug)
                : [...f.product_slugs, slug],
        }));
    };

    const toggleCat = (slug) => {
        setForm((f) => ({
            ...f,
            category_slugs: f.category_slugs.includes(slug)
                ? f.category_slugs.filter((s) => s !== slug)
                : [...f.category_slugs, slug],
        }));
    };

    return (
        <div data-testid="admin-discounts-panel">
            {/* Toolbar */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2 glass rounded-full px-4 py-2 border border-white/10 flex-1 min-w-[260px]">
                    <Search size={14} className="text-[var(--text-muted)]" />
                    <input
                        type="text"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Search codes…"
                        className="bg-transparent text-sm text-white flex-1 focus:outline-none"
                        data-testid="admin-discounts-search"
                    />
                </div>
                <button onClick={load} className="btn-neon" data-testid="admin-discounts-refresh">
                    <RefreshCw size={14} /> Refresh
                </button>
                <button onClick={openNew} className="btn-solid" data-testid="admin-discount-new">
                    <Plus size={14} /> New code
                </button>
            </div>

            {/* Status filter */}
            <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
                {["all", "active", "scheduled", "expired", "disabled"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`shrink-0 px-3.5 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border transition-colors ${
                            statusFilter === s
                                ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]"
                                : "border-white/10 text-[var(--text-secondary)] hover:border-white/20"
                        }`}
                        data-testid={`admin-discount-status-${s}`}
                    >
                        {s === "all" ? "All" : STATUS_PILL[s]?.label || s}
                    </button>
                ))}
            </div>

            {/* List */}
            {loading ? (
                <div className="text-center py-12 text-[var(--text-secondary)]">Loading discounts…</div>
            ) : filtered.length === 0 ? (
                <div className="glass rounded-2xl p-10 text-center text-[var(--text-secondary)]" data-testid="admin-discounts-empty">
                    <Tag size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
                    No discount codes match these filters.
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map((d) => {
                        const pill = STATUS_PILL[d.status] || STATUS_PILL.active;
                        const valueLabel = d.discount_type === "percentage"
                            ? `${d.value}% off`
                            : d.discount_type === "fixed"
                                ? `A$${Number(d.value).toFixed(2)} off`
                                : "Free shipping";
                        const scope = d.applies_to === "all"
                            ? "Entire store"
                            : d.applies_to === "categories"
                                ? `${(d.category_slugs || []).length} categories`
                                : `${(d.product_slugs || []).length} products`;
                        return (
                            <div
                                key={d.code}
                                className="glass-elevated rounded-2xl p-4"
                                data-testid={`admin-discount-row-${d.code}`}
                            >
                                <div className="flex items-start gap-4 flex-wrap">
                                    <div className="flex-1 min-w-[260px]">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-white font-bold tracking-wider text-base">{d.code}</span>
                                            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${pill.cls}`}>
                                                {pill.label}
                                            </span>
                                        </div>
                                        {d.description && (
                                            <div className="text-xs text-[var(--text-secondary)] mt-0.5">{d.description}</div>
                                        )}
                                        <div className="flex items-center gap-3 mt-2 text-xs text-[var(--text-secondary)] flex-wrap">
                                            <span className="font-mono text-[var(--gold)]">{valueLabel}</span>
                                            <span>·</span>
                                            <span>{scope}</span>
                                            {d.min_order_value > 0 && (
                                                <>
                                                    <span>·</span>
                                                    <span>Min A${Number(d.min_order_value).toFixed(2)}</span>
                                                </>
                                            )}
                                            {d.usage_limit && (
                                                <>
                                                    <span>·</span>
                                                    <span>{d.usage_count || 0}/{d.usage_limit} used</span>
                                                </>
                                            )}
                                            {(d.starts_at || d.ends_at) && (
                                                <>
                                                    <span>·</span>
                                                    <span className="inline-flex items-center gap-1">
                                                        <Calendar size={11} />
                                                        {d.starts_at && new Date(d.starts_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                                                        {d.starts_at && d.ends_at && " → "}
                                                        {d.ends_at && new Date(d.ends_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => toggle(d.code)}
                                            className={`px-3 py-1.5 rounded-full border text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1 ${
                                                d.is_active
                                                    ? "border-white/10 text-[var(--text-secondary)] hover:border-red-500/40 hover:text-red-300"
                                                    : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                                            }`}
                                            data-testid={`admin-discount-toggle-${d.code}`}
                                        >
                                            <Power size={11} /> {d.is_active ? "Disable" : "Enable"}
                                        </button>
                                        <button
                                            onClick={() => openEdit(d)}
                                            className="px-3 py-1.5 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                            data-testid={`admin-discount-edit-${d.code}`}
                                        >
                                            <Edit3 size={11} /> Edit
                                        </button>
                                        <button
                                            onClick={() => del(d.code)}
                                            className="p-2 text-[var(--text-muted)] hover:text-red-400"
                                            data-testid={`admin-discount-delete-${d.code}`}
                                            aria-label="Delete"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Editor modal */}
            {editing && (
                <ModalPortal>
                <div
                    className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setEditing(null)}
                    data-testid="admin-discount-editor"
                >
                    <div
                        className="glass-elevated rounded-2xl p-6 max-w-2xl w-full max-h-[88vh] overflow-y-auto border border-[var(--gold)]/30"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="font-display text-xl font-bold text-white">
                                {editing === "new" ? "New discount code" : `Edit · ${editing}`}
                            </h3>
                            <button
                                onClick={() => setEditing(null)}
                                className="p-2 text-[var(--text-muted)] hover:text-white"
                                aria-label="Close"
                                data-testid="admin-discount-close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Code (auto-uppercased)</label>
                                    <input
                                        type="text"
                                        value={form.code}
                                        onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                        placeholder="BLACKFRI2026"
                                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)] font-mono uppercase tracking-wider"
                                        data-testid="discount-form-code"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Internal description (optional)</label>
                                    <input
                                        type="text"
                                        value={form.description}
                                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                                        placeholder="Black Friday 25% off"
                                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="discount-form-description"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2 block">Type</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {DISCOUNT_TYPES.map((t) => (
                                        <button
                                            key={t.id}
                                            onClick={() => setForm({ ...form, discount_type: t.id })}
                                            type="button"
                                            className={`px-3 py-2.5 rounded-xl border text-left text-xs font-mono uppercase tracking-wider transition-colors ${
                                                form.discount_type === t.id
                                                    ? "border-[var(--gold)] bg-[rgba(233,188,11,0.1)] text-[var(--gold)]"
                                                    : "border-white/10 text-[var(--text-secondary)] hover:border-white/30"
                                            }`}
                                            data-testid={`discount-form-type-${t.id}`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {form.discount_type !== "free_shipping" && (
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                                        {form.discount_type === "percentage" ? "Percent off (0–100)" : "Amount off (AUD)"}
                                    </label>
                                    <input
                                        type="number"
                                        step={form.discount_type === "percentage" ? "1" : "0.01"}
                                        value={form.value}
                                        onChange={(e) => setForm({ ...form, value: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="discount-form-value"
                                    />
                                </div>
                            )}

                            <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Starts at (optional)</label>
                                    <input
                                        type="datetime-local"
                                        value={form.starts_at}
                                        onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="discount-form-starts"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Ends at (optional)</label>
                                    <input
                                        type="datetime-local"
                                        value={form.ends_at}
                                        onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="discount-form-ends"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Minimum order value (optional)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={form.min_order_value}
                                    onChange={(e) => setForm({ ...form, min_order_value: e.target.value })}
                                    placeholder="e.g. 100"
                                    className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                    data-testid="discount-form-mov"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2 block">Applies to</label>
                                <div className="grid grid-cols-3 gap-2 mb-3">
                                    {APPLIES_TO.map((s) => (
                                        <button
                                            key={s.id}
                                            onClick={() => setForm({ ...form, applies_to: s.id })}
                                            type="button"
                                            className={`px-3 py-2 rounded-xl border text-xs font-mono uppercase tracking-wider transition-colors ${
                                                form.applies_to === s.id
                                                    ? "border-[var(--gold)] bg-[rgba(233,188,11,0.1)] text-[var(--gold)]"
                                                    : "border-white/10 text-[var(--text-secondary)] hover:border-white/30"
                                            }`}
                                            data-testid={`discount-form-applies-${s.id}`}
                                        >
                                            {s.label}
                                        </button>
                                    ))}
                                </div>
                                {form.applies_to === "categories" && (
                                    <div className="flex flex-wrap gap-2" data-testid="discount-form-categories">
                                        {categories.map((c) => {
                                            const on = form.category_slugs.includes(c.slug);
                                            return (
                                                <button
                                                    key={c.slug}
                                                    onClick={() => toggleCat(c.slug)}
                                                    type="button"
                                                    className={`px-3 py-1.5 rounded-full border text-xs font-mono uppercase tracking-wider ${
                                                        on
                                                            ? "border-[var(--gold)] bg-[rgba(233,188,11,0.1)] text-[var(--gold)]"
                                                            : "border-white/10 text-[var(--text-secondary)]"
                                                    }`}
                                                    data-testid={`discount-form-cat-${c.slug}`}
                                                >
                                                    {c.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                {form.applies_to === "products" && (
                                    <div className="max-h-48 overflow-y-auto glass rounded-xl p-2 space-y-1" data-testid="discount-form-products">
                                        {products.map((p) => {
                                            const on = form.product_slugs.includes(p.slug);
                                            return (
                                                <label
                                                    key={p.slug}
                                                    className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer text-sm"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={on}
                                                        onChange={() => toggleSlug(p.slug)}
                                                        className="accent-[var(--gold)]"
                                                        data-testid={`discount-form-prod-${p.slug}`}
                                                    />
                                                    <span className="text-white flex-1">{p.name}</span>
                                                    <span className="text-[var(--text-muted)] font-mono text-xs">A${Number(p.price).toFixed(2)}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Total uses limit (optional)</label>
                                    <input
                                        type="number"
                                        value={form.usage_limit}
                                        onChange={(e) => setForm({ ...form, usage_limit: e.target.value })}
                                        placeholder="Unlimited"
                                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="discount-form-limit-total"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Uses per customer (optional)</label>
                                    <input
                                        type="number"
                                        value={form.usage_limit_per_customer}
                                        onChange={(e) => setForm({ ...form, usage_limit_per_customer: e.target.value })}
                                        placeholder="Unlimited"
                                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="discount-form-limit-customer"
                                    />
                                </div>
                            </div>

                            <label className="flex items-center justify-between p-3 rounded-xl glass border border-white/5 cursor-pointer">
                                <span className="text-sm text-white">Active</span>
                                <input
                                    type="checkbox"
                                    checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                    className="w-5 h-5 accent-[var(--gold)]"
                                    data-testid="discount-form-active"
                                />
                            </label>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={save}
                                disabled={saving}
                                className="btn-solid flex-1"
                                data-testid="discount-form-save"
                            >
                                <Save size={14} /> {saving ? "Saving…" : "Save code"}
                            </button>
                            <button
                                onClick={() => setEditing(null)}
                                className="btn-ghost"
                                data-testid="discount-form-cancel"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
                </ModalPortal>
            )}
        </div>
    );
};
